/**
 * Embudo DualCTAVideoLanding — visitor journey, recovery email, checkout Stripe/MP.
 * Complementa trackDualCta() en la página con `visitor_journey_realtime` y DB recovery.
 */

import {
  PREMIUM_INVITE_FLOW_NAME,
  PREMIUM_WEBSITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { DATAFAST_GOALS, trackDataFastGoal } from "@/lib/datafastAnalytics";
import {
  getPostHogSlackSessionPropertiesPostHog,
  getSlackFunnelDiagnosticsExtraPostHog,
  isPostHogConfigured,
  trackVipCheckoutStartedPostHog,
} from "@/lib/posthogAnalytics";
import {
  captureVisitorJourneyRealtime,
  getJourneyPayloadForServer,
  getPostHogIdsForServer,
  recordJourneyStep,
  setJourneyRecoveryEmail,
  type JourneyIntentLevel,
} from "@/lib/posthogVisitorJourney";

export const DUAL_CTA_TEMPLATE = "DualCTAVideoLanding";
export const DUAL_CTA_RECOVERY_EMAIL_KEY = "ph_recovery_email_v1";

let journeyContext: { pathname: string; country: string } | null = null;
let dualCtaConverted = false;
const muxErrorNoConvertDedup = new Set<string>();

export function markDualCtaConverted(): void {
  dualCtaConverted = true;
}

export function hasDualCtaConverted(): boolean {
  return dualCtaConverted;
}

/** Superficie PostHog para errores Mux en DualCTA (MuxVideoPlayerWithCTA). */
export const DUAL_CTA_MUX_ANALYTICS_SURFACE = "dual_cta_video_landing";

/** Contexto de ruta/país para mirror automático desde trackDualCta(). */
export function setDualCtaJourneyContext(ctx: {
  pathname: string;
  country: string;
}): void {
  journeyContext = ctx;
}

export function getDualCtaJourneyContext(): typeof journeyContext {
  return journeyContext;
}

export type DualCtaFlowSurface =
  | "dual_cta_home"
  | "dual_cta_video_landing"
  | "premium_invite_landing";

export function resolveDualCtaFlow(pathname: string): {
  flowName: string;
  productSurface: DualCtaFlowSurface;
} {
  if (pathname.includes("/invitacion-premium")) {
    return {
      flowName: PREMIUM_INVITE_FLOW_NAME,
      productSurface: "premium_invite_landing",
    };
  }
  if (pathname === "/") {
    return {
      flowName: PREMIUM_WEBSITE_FLOW_NAME,
      productSurface: "dual_cta_home",
    };
  }
  return {
    flowName: PREMIUM_WEBSITE_FLOW_NAME,
    productSurface: "dual_cta_video_landing",
  };
}

/** Email conocido (Clerk o sesión previa) para recovery antes del redirect Stripe/MP. */
export function getCheckoutEmailForDualCta(clerkEmail?: string | null): string {
  const fromClerk = clerkEmail?.trim();
  if (fromClerk && fromClerk.includes("@")) return fromClerk.toLowerCase();
  if (typeof sessionStorage === "undefined") return "";
  try {
    const stored = sessionStorage.getItem(DUAL_CTA_RECOVERY_EMAIL_KEY)?.trim();
    if (stored && stored.includes("@")) return stored.toLowerCase();
  } catch {
    /* private mode */
  }
  return "";
}

function journeyExtras(
  props?: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!props) return out;
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Dual CTA PostHog event → DataFast custom goals (funnels / Journey).
 * @see https://datafa.st/docs/custom-goals
 * @see https://datafa.st/docs/conversion-funnels
 *
 * initiate_checkout is only fired from trackDualCtaStripeCheckoutStarted
 * (avoids double-count with dual_cta_checkout_initiated).
 */
const EVENT_DATAFAST_GOAL_MAP: Partial<Record<string, string>> = {
  dual_cta_video_viewed: DATAFAST_GOALS.dualCtaView,
  dual_cta_vip_cta_impression: DATAFAST_GOALS.vipCtaImpression,
  dual_cta_video_premium_click: DATAFAST_GOALS.vipCtaClick,
  dual_cta_video_free_click: DATAFAST_GOALS.freeCtaClick,
  dual_cta_video_started: DATAFAST_GOALS.videoStarted,
  dual_cta_video_ended: DATAFAST_GOALS.videoEnded,
};

const EVENT_JOURNEY_MAP: Partial<
  Record<
    string,
    { eventKind: string; recordStep?: string; intentLevel?: JourneyIntentLevel }
  >
> = {
  dual_cta_video_viewed: {
    eventKind: "page_enter_dual_cta_landing",
    recordStep: "dual_cta_view",
    intentLevel: "visit",
  },
  dual_cta_vip_cta_impression: {
    eventKind: "dual_cta_vip_impression",
    recordStep: "vip_cta_seen",
    intentLevel: "engaged",
  },
  /** Video buffering — CTA remains primary; feeds xAI waiting_for_play */
  dual_cta_video_waiting_for_play: {
    eventKind: "dual_cta_waiting_for_play",
    recordStep: "waiting_for_play",
    intentLevel: "engaged",
  },
  dual_cta_video_premium_click: {
    eventKind: "cta_click_vip",
    recordStep: "vip_cta_click",
    intentLevel: "high_intent",
  },
  dual_cta_email_captured: {
    eventKind: "dual_cta_email_captured",
    recordStep: "email_captured_pre_checkout",
    intentLevel: "high_intent",
  },
  dual_cta_checkout_initiated: {
    eventKind: "stripe_checkout_initiated",
    recordStep: "stripe_checkout_redirect",
    intentLevel: "almost_bought",
  },
  dual_cta_video_free_click: {
    eventKind: "cta_click_free_telegram",
    recordStep: "free_telegram_click",
    intentLevel: "high_intent",
  },
  dual_cta_page_exit: {
    eventKind: "engagement_dropoff",
    intentLevel: "abandoned",
  },
  dual_cta_no_action_timeout: {
    eventKind: "dual_cta_idle_timeout",
    recordStep: "idle_15s",
    intentLevel: "engaged",
  },
  dual_cta_video_started: {
    eventKind: "dual_cta_video_play",
    recordStep: "video_started",
    intentLevel: "engaged",
  },
  dual_cta_video_ended: {
    eventKind: "dual_cta_video_complete",
    recordStep: "video_ended",
    intentLevel: "engaged",
  },
  dual_cta_video_error: {
    eventKind: "mux_playback_error",
    recordStep: "mux_video_error",
    intentLevel: "engaged",
  },
  dual_cta_mux_error_no_conversion: {
    eventKind: "mux_playback_error",
    recordStep: "mux_error_no_conversion",
    intentLevel: "abandoned",
  },
  /** HTTP / non-secure DualCTA landing (IG IAB) — must reach Slack. */
  dual_cta_insecure_context: {
    eventKind: "mux_playback_error",
    recordStep: "insecure_page_context",
    intentLevel: "engaged",
  },
};

/** Llamado al final de trackDualCta() si hay contexto de página. */
export function mirrorDualCtaEventFromContext(
  event: string,
  props?: Record<string, unknown>,
): void {
  if (!journeyContext) return;
  mirrorDualCtaEventToJourney(event, props, journeyContext);
}

/** Espeja eventos dual_cta_* a visitor_journey_realtime (PostHog) + goals DataFast. */
export function mirrorDualCtaEventToJourney(
  event: string,
  props: Record<string, unknown> | undefined,
  ctx: { pathname: string; country: string },
): void {
  const mapping = EVENT_JOURNEY_MAP[event];
  if (!mapping) return;
  const { flowName, productSurface } = resolveDualCtaFlow(ctx.pathname);
  captureVisitorJourneyRealtime({
    eventKind: mapping.eventKind,
    template: DUAL_CTA_TEMPLATE,
    country: ctx.country,
    funnel: flowName,
    productSurface,
    recordStep: mapping.recordStep,
    intentLevel: mapping.intentLevel,
    replayNearNow:
      event === "dual_cta_video_premium_click" ||
      event === "dual_cta_checkout_initiated" ||
      event === "dual_cta_page_exit",
    extra: journeyExtras(props),
  });

  const dfGoal = EVENT_DATAFAST_GOAL_MAP[event];
  if (dfGoal) {
    trackDataFastGoal(dfGoal, {
      country: ctx.country,
      flow: flowName,
      surface: productSurface,
    });
  }
}

/** Registra entrada al landing (session journey + step). */
export function recordDualCtaSessionEnter(pathname: string): void {
  const { flowName, productSurface } = resolveDualCtaFlow(pathname);
  recordJourneyStep("dual_cta_enter", {
    funnel: flowName,
    surface: productSurface,
    intentLevel: "visit",
  });
}

/** Persiste email para recovery (MP/Stripe) — fire-and-forget. */
export function persistDualCtaCheckoutEmail(opts: {
  email: string;
  country: string;
  flowName: string;
  provider: "stripe" | "mercadopago";
  amount?: number;
  currency?: string;
  planTier?: string;
}): void {
  const email = opts.email.trim().toLowerCase();
  if (!email.includes("@")) return;
  setJourneyRecoveryEmail(email);
  try {
    sessionStorage.setItem(DUAL_CTA_RECOVERY_EMAIL_KEY, email);
  } catch {}
  void fetch("/api/capture-checkout-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      email,
      plan_tier: opts.planTier ?? "vip",
      flow_name: opts.flowName,
      country: opts.country,
      provider: opts.provider,
      amount: opts.amount,
      currency: opts.currency,
      landing_path:
        typeof window !== "undefined" ? window.location.pathname.slice(0, 180) : undefined,
      ...getPostHogIdsForServer(),
      ...getJourneyPayloadForServer(),
    }),
  }).catch(() => {});
}

/** Casi compra: sesión Stripe creada / redirect a Checkout (intl DualCTA). */
export function trackDualCtaStripeCheckoutStarted(opts: {
  pathname: string;
  country: string;
  amount: number;
  currency: "USD" | "EUR" | "ARS";
  gateway: "stripe" | "fan_fallback";
  hasEmail: boolean;
  flowTraceId?: string;
  stripePriceId?: string;
  /** promo_… aplicado en Checkout (DELFU80 exterior / legacy). */
  stripePromotionCodeId?: string;
  /** Ciudad CF (CL high-purchasing). */
  city?: string;
  /** mx_country | cl_high_purchasing | exterior_xx | … */
  vipPromoSource?: string;
  /** Full buildVipPromoTrackingProps() output — track ALL promo dimensions. */
  promoTrackingExtras?: Record<string, string | number | boolean>;
}): void {
  const { flowName, productSurface } = resolveDualCtaFlow(opts.pathname);
  const currencyLabel = opts.currency;
  const promo =
    typeof opts.stripePromotionCodeId === "string" &&
    opts.stripePromotionCodeId.trim().startsWith("promo_")
      ? opts.stripePromotionCodeId.trim().slice(0, 80)
      : undefined;
  const fromFull = opts.promoTrackingExtras || {};
  const promoExtras: Record<string, string | number | boolean> = {
    ...fromFull,
    ...(promo ? { vip_stripe_promotion_code_id: promo, promotion_code_id: promo } : {}),
    ...(opts.city?.trim() ? { effective_city: opts.city.trim().slice(0, 80) } : {}),
    ...(opts.vipPromoSource
      ? { vip_promo_source: opts.vipPromoSource.slice(0, 40) }
      : {}),
  };
  // PostHog VIP started accepts string|boolean extras in some paths — coerce numbers to string for that API.
  const promoExtrasPh: Record<string, string | boolean> = {};
  for (const [k, v] of Object.entries(promoExtras)) {
    if (typeof v === "number") promoExtrasPh[k] = String(v);
    else promoExtrasPh[k] = v;
  }

  trackVipCheckoutStartedPostHog(
    "VIP",
    opts.amount,
    currencyLabel,
    opts.country,
    opts.gateway === "stripe" ? "stripe" : "stripe",
    opts.hasEmail,
    false,
    false,
    promoExtrasPh,
  );

  // Dual CTA VIP = Stripe subscription MRR (Exclusive Member), not one-time PI.
  // Brand (BRAND_SOURCE_KEY) auto-merged in trackDataFastGoal for multi-creator MRR.
  trackDataFastGoal(DATAFAST_GOALS.initiateCheckout, {
    country: opts.country,
    currency: currencyLabel,
    gateway: opts.gateway,
    flow: flowName,
    surface: productSurface,
    plan_tier: "vip",
    revenue_type: "mrr",
    billing: "month",
    product: "exclusive_member",
    amount_usd: String(opts.amount),
    first_month_usd: promoExtras.exterior_vip_delfu80 ? "8" : String(opts.amount),
    ...(promo ? { promotion_code_id: promo } : {}),
    ...(opts.vipPromoSource ? { vip_promo_source: opts.vipPromoSource } : {}),
    ...(promoExtras.vip_promo_code_name
      ? { vip_promo_code_name: String(promoExtras.vip_promo_code_name) }
      : {}),
    ...(promoExtras.vip_first_month_discount_percent != null
      ? {
          vip_first_month_discount_percent: String(
            promoExtras.vip_first_month_discount_percent,
          ),
        }
      : {}),
  });

  captureVisitorJourneyRealtime({
    eventKind: "stripe_checkout_initiated",
    template: DUAL_CTA_TEMPLATE,
    country: opts.country,
    funnel: flowName,
    productSurface,
    recordStep: "stripe_checkout_redirect",
    intentLevel: "almost_bought",
    replayNearNow: true,
    extra: {
      gateway: opts.gateway,
      has_checkout_email: opts.hasEmail,
      amount: opts.amount,
      currency: currencyLabel,
      ...(opts.flowTraceId ? { flow_trace_id: opts.flowTraceId.slice(0, 64) } : {}),
      stripe_price_id: opts.stripePriceId?.startsWith("price_")
        ? opts.stripePriceId.slice(0, 40)
        : "dashboard:plan_tier=vip",
      ...promoExtras,
    },
  });
}

/** Clic VIP sin llegar a Stripe — alerta Slack high_intent_abandonment. */
export function postDualCtaHighIntentAbandonment(opts: {
  sessionId: string;
  pathname: string;
  country: string;
  secondsOnPage: number;
  videoWatchedSeconds: number;
  extra?: Record<string, string | number | boolean>;
}): void {
  if (typeof window === "undefined") return;
  const { flowName, productSurface } = resolveDualCtaFlow(opts.pathname);
  const phCtx = getPostHogSlackSessionPropertiesPostHog({ replayNearNow: true });
  const funnelDiag = getSlackFunnelDiagnosticsExtraPostHog({ replayNearNow: true });
  const journeyPayload = getJourneyPayloadForServer();

  captureVisitorJourneyRealtime({
    eventKind: "high_intent_abandonment",
    template: DUAL_CTA_TEMPLATE,
    country: opts.country,
    funnel: flowName,
    productSurface,
    recordStep: "vip_click_no_checkout",
    intentLevel: "abandoned",
    replayNearNow: true,
    extra: {
      seconds_on_page: opts.secondsOnPage,
      video_watched_seconds: opts.videoWatchedSeconds,
      ...journeyPayload,
      ...opts.extra,
    },
  });

  void fetch("/api/track-funnel-failure", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      kind: "high_intent_abandonment",
      template: DUAL_CTA_TEMPLATE,
      country: opts.country || "",
      flow_trace_id: opts.sessionId || undefined,
      page_url: window.location.href,
      extra: {
        hero_cta: "dual_cta",
        funnel: flowName,
        product_surface: productSurface,
        seconds_on_page: opts.secondsOnPage,
        video_watched_seconds: opts.videoWatchedSeconds,
        paid_cta: "vip",
        checkout_started: false,
        ...journeyPayload,
        ...phCtx,
        ...funnelDiag,
        ...opts.extra,
      },
    }),
  }).catch(() => {});
}

export type DualCtaMuxErrorTrigger =
  | "fatal_playback"
  | "page_exit"
  | "mux_player_event";

/**
 * Evento canónico PostHog: error Mux en DualCTA y el visitante NO convirtió.
 * Importante: `converted = false` ≠ `conversion_possible = false`. DualCTA keeps
 * VIP (+ free TG) CTAs usable when the teaser fails — callers should pass
 * `conversion_possible: true` in `extra` when CTAs are still shown.
 */
export function trackDualCtaMuxErrorNoConversion(opts: {
  trigger: DualCtaMuxErrorTrigger;
  pathname: string;
  country: string;
  errorCount: number;
  message?: string;
  muxEvent?: string;
  fatal?: boolean;
  videoStarted?: boolean;
  secondsSinceMount?: number;
  playbackId?: string;
  exitReason?: string;
  extra?: Record<string, string | number | boolean>;
}): void {
  if (typeof window === "undefined") return;
  if (dualCtaConverted) return;

  const dedupeKey = [
    opts.trigger,
    opts.muxEvent ?? "",
    opts.message?.slice(0, 48) ?? "",
    opts.exitReason ?? "",
  ].join("|");
  if (muxErrorNoConvertDedup.has(dedupeKey)) return;
  muxErrorNoConvertDedup.add(dedupeKey);

  const { flowName, productSurface } = resolveDualCtaFlow(opts.pathname);
  const conversionPossible = opts.extra?.conversion_possible !== false;
  const payload: Record<string, unknown> = {
    template: DUAL_CTA_TEMPLATE,
    flow_surface: DUAL_CTA_MUX_ANALYTICS_SURFACE,
    product_surface: productSurface,
    funnel: flowName,
    country: opts.country,
    converted: false,
    conversion_possible: conversionPossible,
    dual_cta_ctas_usable: conversionPossible,
    surface_dual_cta: true,
    trigger: opts.trigger,
    error_count: opts.errorCount,
    fatal: opts.fatal ?? false,
    video_started: opts.videoStarted ?? false,
    ...(opts.message ? { error_message: opts.message.slice(0, 500) } : {}),
    ...(opts.muxEvent ? { mux_event: opts.muxEvent } : {}),
    ...(opts.secondsSinceMount != null
      ? { seconds_since_mount: opts.secondsSinceMount }
      : {}),
    ...(opts.playbackId
      ? {
          playback_id: opts.playbackId,
          playback_id_short: opts.playbackId.slice(-8),
        }
      : {}),
    ...(opts.exitReason ? { exit_reason: opts.exitReason } : {}),
    ...opts.extra,
  };

  if (isPostHogConfigured()) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ph = (window as any).posthog;
      ph?.capture?.("dual_cta_mux_error_no_conversion", payload);
      ph?.capture?.("conversion_funnel_step", {
        funnel_step: "mux_error_no_conversion",
        converted: false,
        ...payload,
      });
      if (opts.trigger === "fatal_playback" || opts.fatal) {
        ph?.startSessionRecording?.();
      }
    } catch {
      /* best-effort */
    }
  }

  mirrorDualCtaEventToJourney("dual_cta_mux_error_no_conversion", payload, {
    pathname: opts.pathname,
    country: opts.country,
  });
}

/** Llamado desde MuxVideoPlayerWithCTA.logVideoEvent en errores de play/load. */
export function maybeTrackDualCtaMuxPlayerError(opts: {
  analyticsFlowSurface?: string;
  muxEvent: string;
  country?: string;
  playbackId?: string;
  errorMessage?: string;
  errorType?: string;
  pathname?: string;
}): void {
  if (opts.analyticsFlowSurface !== DUAL_CTA_MUX_ANALYTICS_SURFACE) return;
  if (dualCtaConverted) return;
  const ctx = journeyContext;
  const pathname = opts.pathname ?? ctx?.pathname ?? window.location.pathname;
  const country = opts.country ?? ctx?.country ?? "UNKNOWN";
  trackDualCtaMuxErrorNoConversion({
    trigger: "mux_player_event",
    pathname,
    country,
    errorCount: 1,
    muxEvent: opts.muxEvent,
    message: opts.errorMessage ?? opts.errorType,
    fatal: false,
    playbackId: opts.playbackId,
  });
}